import React, {
  useState,
  useMemo,
  useCallback,
  Suspense,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Color, FontWeight, Size } from 'lib/styles';
import { Main, Background, Border } from 'styles/themeColors';
import Product, * as Prod from 'data/Product';
import Variant from 'data/Variant';
import { getEmptyInventoryErrors, VariantError } from 'data/ProductValidation';
import { unique } from 'lib/filters';
import { TEXT_LABELS } from 'constants/productDetails';
import Currency from 'data/Currency';
import Country from 'data/Country';
import Vendor from 'data/Vendor';
import FeatureFlag from 'data/FeatureFlag';
import User from 'data/User';
import Checkbox from 'components/UI/Checkbox';
import {
  getBasicColumns,
  getTypeColumns,
  getColumnMetaData,
  getVariantRegionPurchasePrice,
  getRegionCountryId,
  commissionAmountFromPercentage,
  commissionPercentageFromAmount,
  countriesIncludeRegion,
  getCommissionColumnKeys,
  buildCommissionColumnGroups,
  syncVariantCommissionFields,
  applyRentalColumnValue,
  isRentalColumnKey,
} from 'utils/variant';
import { useAuth } from 'lib/Auth';
import SortingArrow from 'view/SortingArrow';
import { Sorting } from 'common/ProductDetailsV2';
import { Row } from 'common/VariantTableRowV2';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { PRICE_TYPE, PRODUCT_TYPE } from 'constants/product';
import { COUNTRY_ISO } from 'constants/country';
import { useReadPermission } from 'hooks/useUserRoles';
import { PRODUCT_STATUS } from 'constants/product';
import { getProductTypeConfig } from 'utils/product';
import { ACTION_TYPE } from 'constants/aiVariantImport';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { UnitOfMeasurement } from 'data/UnitOfMeasurement';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';
import Attribute from 'data/Attribute';
import Pagination from 'components/UI/Pagination';
import { smartVariantsPaginationSelectOptions } from 'constants/paginationOptions';
import { applyPaginationFilter, calculatePages } from 'utils/grid';

const VariantTableRowV2 = React.lazy(() => import('common/VariantTableRowV2'));

const CollapsibleTableContainer = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-bottom: 1.5rem;
  width: 100%;
  box-sizing: border-box;
`;

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const CollapsibleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CollapsibleTitleText = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${Main.brandLight};
  border: 1px solid ${Main.lightGray};
  border-radius: 0.375rem;
  color: ${Main.white};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${Main.brandLight};
    border-color: ${Main.brandLight};
  }
`;

const CollapsibleContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${(props) => (props.isExpanded ? 'none' : '0')};
  overflow: ${(props) => (props.isExpanded ? 'visible' : 'hidden')};
  transition: max-height 0.3s ease-in-out;
  padding-bottom: ${(props) => (props.isExpanded ? '1.5rem' : '0')};
`;

const BaseTable = styled.table`
  border-spacing: 0;
  color: ${Main.accent};
  text-align: left;
  width: 100%;
  border: none !important;
  border-radius: 10px;
`;

const Container = styled.div`
  -webkit-overflow-scrolling: touch;
  margin-left: -2.25rem;
  // margin-top: ${Size.large};
  overflow-x: auto;
  display: flex;
  overflow-y: auto;
  max-height: 70vh;
  width: calc(100% + 2.25rem);
  box-sizing: border-box;
`;

const Th = styled.th<any>`
  background-color: ${Main.white};
  padding: 10px;
  position: relative;
  white-space: nowrap;
  left: 0;
  border-top: 1px solid ${Border.light};
  color: ${Color.black};
  text-align: ${({ $isCheckbox }) => $isCheckbox ? 'center' : 'left'};
`;

const ThParent = styled.th<any>`
  background-color: ${Background.light};
  color: ${Color.black};
  padding: 10px;
  position: relative;
  white-space: nowrap;
  left: 0;
  font-weight: 100;
`;

const IconContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  cursor: pointer;
  margin-right: 5px;
  vertical-align: top;
`;

const ThAction = styled.th`
  font-size: 0;
`;

const ThContentsParent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const ThContents = styled.div`
  align-items: center;
  display: flex;
  font-weight: ${FontWeight.bold};
  justify-content: center;
  width: 100%;
`;

const Headers = styled.tr`
  th:last-parent {
    border-right: 1px solid ${Border.light};
    border-top-right-radius: 10px;
  }
  th:last-child {
    border-right: 1px solid ${Border.light};
  }
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 99;
  background-color: #ffffff;
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  padding: 2rem;
`;

const KEYS_FOR_SORT = ['checkbox'];
const COMMISSION_FIELD_REGEX = /^(affiliateCommission|commissionAmount)(US|CA)$/;

interface LocalVariant {
  attributes?: Array<{
    attribute_code: string;
    attribute_value: string;
  }>;
  [key: string]: any;
}

type ActionType = 'create' | 'update' | 'delete' | 'bulk';

interface AIResponse {
  actionType: ActionType;
  data: any;
}

interface Props {
  variants: LocalVariant[];
  actionType?: AIResponse['actionType'];
  product: Product | null;
  featureFlags: FeatureFlag;
  currencies: Currency[];
  countries: Country[];
  selectedVendor: Vendor;
  user: User | null;
  tableTitle?: string;
  isSelectable?: boolean;
  isEditable?: boolean;
  setSelectedRows?: (selectedRows: any) => void;
  selectedRows?: any;
  vendorAttributes: Attribute[];

}

const GeneratedVariantsTable: React.FC<Props> = ({
  variants,
  actionType,
  product,
  featureFlags,
  currencies,
  countries,
  selectedVendor,
  user,
  tableTitle,
  isSelectable,
  isEditable,
  setSelectedRows,
  selectedRows,
  vendorAttributes,
}) => {
  const { user: authUser } = useAuth();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  const [allVariantsSelection, setAllVariantsSelection] =
    useState<boolean>(false);
  const [sortByHeader, setSortByHeader] = useState<Sorting | undefined>(
    undefined
  );
  const [localVariants, setLocalVariants] = useState<Variant[]>([]);
  const [suggestionValuesList, setSuggestionValuesList] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);

  // Load units of measurement from session storage
  const unitsOfMeasurement: UnitOfMeasurement[] = useMemo(
    () => getItems(STORAGE_KEYS.UNIT_OF_MEASUREMENTS) || [],
    []
  );

  const typeConfig = getProductTypeConfig(product?.productType);
  const { readOnly: baseDeleteReadOnly } = useReadPermission(
    typeConfig.permissions.variantsDelete
  );
  const isDraftStatus = product?.status === PRODUCT_STATUS.DRAFT;
  const isDeleteReadOnly = isDraftStatus && baseDeleteReadOnly;

  // Initialize and sync local variants state from props
  useEffect(() => {
    if (variants && variants.length > 0) {
      // Deep clone variants to avoid mutating props
      let clonedVariants = variants.map((variant) => ({
        ...variant,
      })) as Variant[];

      if (actionType === ACTION_TYPE.CREATE) {
        clonedVariants = clonedVariants.map((variant, index) => ({
          ...variant,
          id: index + 1,
        }));
      }

      // A generated change to one side of a commission pair (amount / percentage) leaves the other
      // side holding its previous value, so reconcile against the saved variant before displaying.
      if (actionType !== ACTION_TYPE.DELETE) {
        clonedVariants = clonedVariants.map((variant) =>
          syncVariantCommissionFields(
            variant,
            countries,
            actionType === ACTION_TYPE.CREATE
              ? null
              : product?.variants?.find((v: Variant) => v.id === variant.id)
          )
        );
      }

      setLocalVariants(clonedVariants);
    } else {
      setLocalVariants([]);
    }
    setCurrentPage(1);
  }, [variants]);

  // Use local variants state instead of props
  const mappedVariants = useMemo(() => {
    return localVariants;
  }, [localVariants]);

  const getTableRows = (): Row[] => {
    if (!product) return [];

    const InventoryError = getEmptyInventoryErrors(product);
    const emptyVariantErrors: VariantError[] = [];

    return mappedVariants.map((variant) => {
      return {
        variant,
        errorInventoryTypes: InventoryError.filter((error) =>
          error.variants.map((vari) => vari.id).includes(variant.id)
        )
          .map((error) => error.type)
          .filter(unique),
        errorTypes: emptyVariantErrors
          .filter((error) =>
            error.variants.map((vari) => vari.id).includes(variant.id)
          )
          .map((error) => error.type)
          .filter(unique),
        focused: true,
      };
    });
  };

  // Blur validation for unit of measurement
  const onUnitOfMeasurementBlur = (value: string): string | null => {
    const foundUom = unitsOfMeasurement.find((uom) =>
      uom?.displayName?.toLowerCase() === value?.toLowerCase() ||
      uom?.shortName?.toLowerCase() === value?.toLowerCase() ||
      uom?.fullName?.toLowerCase() === value?.toLowerCase() ||
      uom?.unitOfMeasurementVariations?.some(v => v.value.toLowerCase() === value?.toLowerCase())
    );

    return foundUom ? foundUom.displayName : null;
  };

  // Blur reducer for validation
  const onCellBlur = (key: string, value: string): string => {
    if (key.includes(TEXT_LABELS.RANGE_UNIT)) {
      return onUnitOfMeasurementBlur(value) || value;
    }
    return value;
  };

  // Clear suggestions
  const clearSuggestions = () => {
    setSuggestionValuesList([]);
  };

  const handleCellEdit = (
    newAttributeValue: string | number | boolean,
    variant: Variant,
    attributeKey: string,
    keyId?: number,
    extras?: any
  ) => {
    // Handle checkbox selection (outside setState to avoid timing issues)
    if (attributeKey === 'checkbox') {
      const alreadySelected = selectedVariants.find(
        (id) => variant.id === id
      );
      if (alreadySelected) {
        setSelectedVariants(
          selectedVariants.filter((id) => id !== variant.id)
        );
      } else {
        variant.id && setSelectedVariants([...selectedVariants, variant.id]);
      }
      return;
    }

    // Validation: Check if product is hidden and prevent variant unhiding
    if (attributeKey === TEXT_LABELS.HIDDEN && product?.hidden === true) {
      renderToast(toast.TYPE.INFO, TEXT_LABELS.PRODUCT_HIDDEN_MESSAGE);
      return;
    }

    setLocalVariants((prevVariants) => {
      const variantIndex = prevVariants.findIndex((v) => v.id === variant.id);
      if (variantIndex === -1) return prevVariants;

      const updatedVariants = [...prevVariants];
      const variantToUpdate = { ...updatedVariants[variantIndex] };

      // Reset variant validation status when key fields change
      if (product?.vendor?.isSlbVendor) {
        if (attributeKey === TEXT_LABELS.MATERIAL_NUMBER && variantToUpdate.materialNumber !== newAttributeValue) {
          variantToUpdate.variantValidationStatus = undefined;
        }
      } else {
        if (attributeKey === TEXT_LABELS.SKU_SMALL && variantToUpdate.sku !== newAttributeValue) {
          variantToUpdate.variantValidationStatus = undefined;
        }
      }

      // Handle unit price columns (pricePerUnit with countryId)
      if (attributeKey === 'pricePerUnit' && extras?.countryId) {
        const countryId = extras.countryId;
        const priceValue =
          typeof newAttributeValue === 'number'
            ? newAttributeValue
            : parseFloat(String(newAttributeValue)) || 0;
        if (!variantToUpdate.productVariantPrices) {
          variantToUpdate.productVariantPrices = [];
        }
        const priceIndex = variantToUpdate.productVariantPrices.findIndex(
          (price: any) => price.countryId === countryId
        );
        if (priceIndex !== -1) {
          variantToUpdate.productVariantPrices[priceIndex] = {
            ...variantToUpdate.productVariantPrices[priceIndex],
            pricePerUnit: priceValue,
          };
        } else {
          variantToUpdate.productVariantPrices.push({
            id: keyId || undefined,
            countryId,
            pricePerUnit: priceValue,
          });
        }
        updatedVariants[variantIndex] = variantToUpdate;
        return updatedVariants;
      }

      // Handle warehouse stock columns (available, deliveryLeadDays, postOrderShippingCost)
      if (
        (attributeKey === 'available' ||
          attributeKey === 'deliveryLeadDays' ||
          attributeKey === 'postOrderShippingCost') &&
        extras?.warehouseId
      ) {
        const warehouseId = extras.warehouseId;
        if (!variantToUpdate.productStocks) {
          variantToUpdate.productStocks = [];
        }
        const stockIndex = variantToUpdate.productStocks.findIndex(
          (stock: any) =>
            stock.warehouseId === warehouseId &&
            stock.type === extras?.warehouseType
        );
        if (stockIndex !== -1) {
          variantToUpdate.productStocks[stockIndex] = {
            ...variantToUpdate.productStocks[stockIndex],
            [attributeKey]: newAttributeValue,
          };
        } else {
          variantToUpdate.productStocks.push({
            id: keyId || undefined,
            warehouseId,
            type: extras?.warehouseType,
            [attributeKey]: newAttributeValue,
          });
        }
        updatedVariants[variantIndex] = variantToUpdate;
        return updatedVariants;
      }

      // Handle rental price and unit of measurement columns, both backed by the
      // rentalPriceUS / rentalPriceCA arrays.
      if (isRentalColumnKey(attributeKey)) {
        if (attributeKey.includes(TEXT_LABELS.RANGE_UNIT)) {
          setSuggestionValuesList(
            unitsOfMeasurement
              ?.map((el) => [
                el?.displayName,
                el?.shortName,
                el?.fullName,
                ...el?.unitOfMeasurementVariations?.map(v => v.value)
              ])
              .flat()
              ?.filter((variation) => variation?.toLowerCase()?.includes(newAttributeValue?.toString().toLowerCase()))
              ?.filter(unique)
              ?.sort() || []
          );
        }

        updatedVariants[variantIndex] = applyRentalColumnValue(variantToUpdate, attributeKey, newAttributeValue);
        return updatedVariants;
      }

      // Handle attribute values (selectable attributes)
      // keyId is the attributeId when editing attributes
      if (keyId && product) {
        const foundProductAttribute = product.attributes.find(
          (attr) =>
            attr.id === keyId ||
            attr.name?.toLowerCase() === attributeKey.toLowerCase()
        );

        const foundVendorAttribute = keyId
          ? vendorAttributes.find(
            (attribute) => attribute.id === keyId
          )
          : vendorAttributes.find(
            (attribute) =>
              attribute.name?.toLowerCase() === attributeKey.toLowerCase()
          );

        if (foundProductAttribute && typeof newAttributeValue === 'string') {
          let newValue;

          if (foundVendorAttribute) {
            // Existing attribute: check if value exists in vendor attribute values
            const foundVendorValue = foundVendorAttribute.values.find(
              (value) =>
                value?.name?.toLowerCase() === newAttributeValue?.toLowerCase()
            );

            newValue = foundVendorValue
              ? {
                ...foundVendorValue,
                attributeId: foundVendorAttribute.id,
                displayOrder: 0,
                isSelectable: foundProductAttribute.isSelectable,
              }
              : {
                id: 0,
                attributeId: foundVendorAttribute.id,
                code: '',
                name: newAttributeValue,
                displayOrder: 0,
                isSelectable: foundProductAttribute.isSelectable,
              };
          } else {
            // Newly created attribute: vendorAttribute not loaded yet, use product attribute
            newValue = {
              id: 0,
              attributeId: foundProductAttribute.id,
              code: '',
              name: newAttributeValue,
              displayOrder: 0,
              isSelectable: foundProductAttribute.isSelectable,
            };
          }
          // Populate suggestions from attribute values if available
          if (foundVendorAttribute) {
            setSuggestionValuesList(
              foundVendorAttribute.values
                .map((el) => el?.name?.toLowerCase() || "")
                .filter((el) => el.includes(newAttributeValue.toLowerCase()))
            );
          }

          if (!variantToUpdate.attributeValues) {
            variantToUpdate.attributeValues = [];
          }
          const attrIndex = variantToUpdate.attributeValues.findIndex(
            (attr: any) => attr.attributeId === foundProductAttribute.id
          );

          if (attrIndex !== -1) {
            variantToUpdate.attributeValues[attrIndex] = newValue;
          } else {
            variantToUpdate.attributeValues.push(newValue);
          }
          updatedVariants[variantIndex] = variantToUpdate;
          return updatedVariants;
        }
      }

      // Handle basic variant properties (direct assignment)
      if (attributeKey) {
        (variantToUpdate as any)[attributeKey] = newAttributeValue;

        // Bidirectional commission sync (per region)
        const commissionMatch = COMMISSION_FIELD_REGEX.exec(attributeKey);
        if (commissionMatch && newAttributeValue !== '' && newAttributeValue != null) {
          const [, field, region] = commissionMatch as unknown as [string, 'affiliateCommission' | 'commissionAmount', 'US' | 'CA'];
          const countryId = getRegionCountryId(availableCountriesPurchase, region);
          const price = getVariantRegionPurchasePrice(variantToUpdate, countryId);
          if (price && price > 0) {
            if (field === 'affiliateCommission') {
              (variantToUpdate as any)[`commissionAmount${region}`] = commissionAmountFromPercentage(Number(newAttributeValue), price);
            } else {
              (variantToUpdate as any)[`affiliateCommission${region}`] = commissionPercentageFromAmount(Number(newAttributeValue), price);
            }
          }
        }

        updatedVariants[variantIndex] = variantToUpdate;
        return updatedVariants;
      }

      return prevVariants;
    });
  };

  const isErrorColumn = (key: string, sku: string) => false;

  useEffect(() => {
    if (setSelectedRows) {
      let newVariants: any[] = selectedVariants;
      if (actionType !== 'delete') {
        newVariants = localVariants.filter((variant: any) =>
          selectedVariants.includes(variant.id)
        );
      }
      setSelectedRows({ ...selectedRows, [`${actionType}`]: newVariants });
    }
  }, [allVariantsSelection, selectedVariants]);

  const selectUnselectAllVariants = useCallback(
    (value: boolean) => {
      setAllVariantsSelection(value);
      setSelectedVariants(
        value ? localVariants.map((v: Variant) => v.id as number) : []
      );
    },
    [localVariants]
  );

  const allTableRows = getTableRows();
  const totalPages = calculatePages(allTableRows.length, itemsPerPage);
  const tableRows = applyPaginationFilter(allTableRows, currentPage, itemsPerPage);

  // Memoize expensive productStocks calculation
  const productStocks = useMemo(
    () =>
      mappedVariants?.map((variant: Variant) => variant.productStocks).flat() ||
      [],
    [mappedVariants]
  );

  // Memoize variants rental check
  const hasRentalVariants = useMemo(
    () => mappedVariants.some((x) => x.rent),
    [mappedVariants]
  );

  const productWarehouses = product?.warehouses || [];
  const selectedWarehouses = productWarehouses;

  const warehouseCountriesPurchase = selectedWarehouses
    .filter((x) => x.type === PRICE_TYPE.PURCHASE)
    .map((warehouse: Prod.ProductWarehouse) => warehouse.address.country);

  const warehouseCountriesRent = selectedWarehouses
    .filter((x) => x.type === PRICE_TYPE.RENT)
    .map((warehouse: Prod.ProductWarehouse) => warehouse.address.country);

  // Filtering duplicate countries
  const availableCountriesPurchase = warehouseCountriesPurchase
    .filter(
      (country: Country, index: number, self: any) =>
        index ===
        self.findIndex((t: any) => t?.countryName === country?.countryName)
    )
    .map((country: Country) => {
      const currency = currencies.find(
        (currency: Currency) => currency.countryId === country?.id
      );
      return { ...country, currency };
    });

  const availableCountriesRent = warehouseCountriesRent
    .filter(
      (country: Country, index: number, self: any) =>
        index ===
        self.findIndex((t: any) => t?.countryName === country?.countryName)
    )
    .map((country: Country) => {
      const currency = currencies.find(
        (currency: Currency) => currency.countryId === country?.id
      );
      return { ...country, currency };
    });

  const getRangeUnitColumns = (countries: Country[]) => {
    let columns: any[] = [];
    countries.forEach((country) => {
      if (country.countryIso === COUNTRY_ISO.US) {
        columns.push({
          key: `rangeUnit-${country.countryIso}`,
          label: `${TEXT_LABELS.RANGE_UNIT_US_LABEL}`,
        });
      }
      if (country.countryIso === COUNTRY_ISO.CA) {
        columns.push({
          key: `rangeUnit-${country.countryIso}`,
          label: `${TEXT_LABELS.RANGE_UNIT_CA_LABEL}`,
        });
      }
    });
    return columns;
  };

  const getRentalPriceColumns = (countries: Country[]) => {
    let columns: any[] = [];
    countries.forEach((country) => {
      if (country.countryIso === COUNTRY_ISO.US) {
        columns.push({
          key: `rentalPrice-${country.countryIso}`,
          label: `${TEXT_LABELS.RENTAL_PRICE_US_LABEL}`,
          isRentalPriceColumn: true,
        });
      }
      if (country.countryIso === COUNTRY_ISO.CA) {
        columns.push({
          key: `rentalPrice-${country.countryIso}`,
          label: `${TEXT_LABELS.RENTAL_PRICE_CA_LABEL}`,
          isRentalPriceColumn: true,
        });
      }
    });
    return columns;
  };

  const parentHeaders = useMemo(() => {
    if (!product) return [];

    const getRentalColumns = () => {
      let rentalColumns: any = [];
      if (featureFlags?.rent?.enabled) {
        const rangeUnitColumns = getRangeUnitColumns(availableCountriesRent);
        if (rangeUnitColumns.length) {
          rentalColumns.push({
            key: TEXT_LABELS.RANGE_UNITS,
            name: TEXT_LABELS.UNITS_OF_MEASUREMENT,
            colSpan: rangeUnitColumns.length,
            columns: rangeUnitColumns,
          });
        }
        const rentalPriceColumns = getRentalPriceColumns(
          availableCountriesRent
        );
        if (rentalPriceColumns.length) {
          rentalColumns.push({
            key: TEXT_LABELS.RENTAL_PRICE,
            name: TEXT_LABELS.RENTAL_PRICE_LABEL,
            colSpan: rentalPriceColumns.length,
            columns: rentalPriceColumns,
          });
        }
      }
      return rentalColumns;
    };

    let attributes = product.attributes || [];
    let classifications = attributes.filter((x) => !x.isSelectable);
    let selectableAttributes = attributes.filter((x) => x.isSelectable);

    let basicColumns = getBasicColumns(
      product.brand?.brandVendor!,
      selectedVendor?.isSlbVendor,
      authUser?.role
    );

    const showCommissionColumns = !product.productType || product.productType === PRODUCT_TYPE.REGULAR;
    // Commission only makes sense for a region the product actually has a purchase warehouse in
    const hasUsPurchaseWarehouse = countriesIncludeRegion(availableCountriesPurchase, 'US');
    const hasCaPurchaseWarehouse = countriesIncludeRegion(availableCountriesPurchase, 'CA');
    const commissionColumnsUS = getCommissionColumnKeys('US', showCommissionColumns, hasUsPurchaseWarehouse);
    const commissionColumnsCA = getCommissionColumnKeys('CA', showCommissionColumns, hasCaPurchaseWarehouse);

    let typeColumns = getTypeColumns(featureFlags?.rent?.enabled);

    let unitPricesColumns: any[] = availableCountriesPurchase.map(
      (country) => ({
        label: `${country.countryIso} purchase price`,
        key: `pricePerUnit-${country.id}`,
        isUnitPriceColumn: true,
      })
    );

    const columns: any[] = [
      ...(isSelectable
        ? [
          {
            key: 'action',
            colSpan: 1,
            columns: [
              {
                key: 'checkbox',
                label: (
                  <div
                    style={{
                      display: 'flex',
                      flex: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox
                      id="table-editable-cell-checkbox-id"
                      checkboxSize="xs"
                      appearance="none"
                      checked={allVariantsSelection}
                      onChange={(value) => {
                        selectUnselectAllVariants(value);
                        setAllVariantsSelection(value);
                      }}
                      theme={
                        allVariantsSelection ? 'brandLight' : 'lightGray'
                      }
                      bgColor={
                        allVariantsSelection
                          ? Main.brandLight
                          : Background.light
                      }
                    />
                  </div>
                ),
              },
            ],
          },
        ]
        : []),
      {
        key: 'basic',
        name: 'Basic',
        colSpan: basicColumns.length,
        columns: [...basicColumns.map((key) => getColumnMetaData(key))],
      },
      ...buildCommissionColumnGroups(commissionColumnsUS, commissionColumnsCA),
      {
        key: 'type',
        name: 'Type',
        colSpan: typeColumns.length,
        columns: typeColumns.map((key) => getColumnMetaData(key)),
      },
      {
        key: 'selectableAttributes',
        name: 'Selectable Attributes',
        colSpan: selectableAttributes.length,
        columns: selectableAttributes.map((key) => getColumnMetaData(key)),
      },
      {
        key: 'tehcnicalSpecifications',
        name: 'Technical Specifications',
        colSpan: classifications.length,
        columns: classifications.map((key) => getColumnMetaData(key)),
      },
    ];

    if (hasRentalVariants) {
      const rentalColumns = getRentalColumns();
      rentalColumns.forEach((rc: any) => {
        columns.push(rc);
      });
    }

    if (unitPricesColumns.length) {
      const unitPriceColumn = {
        key: TEXT_LABELS.UNIT_PRICES,
        name: TEXT_LABELS.PURCHASE_UNIT_PRICE,
        colSpan: unitPricesColumns.length,
        columns: unitPricesColumns,
      };
      columns.push(unitPriceColumn);
    }

    return columns;
  }, [
    product,
    selectedVendor,
    authUser?.role,
    featureFlags?.rent?.enabled,
    hasRentalVariants,
    availableCountriesPurchase,
    availableCountriesRent,
    allVariantsSelection,
    selectedVariants,
    isDeleteReadOnly,
    selectUnselectAllVariants,
  ]);

  const columns: any[] = parentHeaders.map((header) => header.columns).flat();

  const columnHeaders = columns.map(({ key, label, keyId }, i) => {
    const sortProduct = (label: string, direction: string) => {
      let priceType = '';
      let sortField = label;
      if (keyId) {
        sortField = `attributeValues-${keyId}`;
      } else if (label.indexOf('pricePerUnit') > -1) {
        sortField = label.replace('pricePerUnit', 'productVariantPrices');
        priceType = PRICE_TYPE.PURCHASE;
      } else if (label.indexOf('rentalPrice') > -1) {
        const countryIso = label?.split?.('-')?.[1];
        const availableCountry = availableCountriesRent.find(
          (x) => x.countryIso === countryIso
        );
        sortField = `productVariantPrices-${availableCountry?.id}`;
        priceType = PRICE_TYPE.RENT;
      }
      setSortByHeader({ header: label, direction, sortField, priceType });
    };
    const isCheckbox = key === 'checkbox';
    return (
      <Th orderIndex={i} key={i} className={i === 0 ? 'th-child' : ''} $isCheckbox={isCheckbox}>
        <ThContents>
          {!KEYS_FOR_SORT.includes(key) && key.indexOf('range') === -1 && (
            <IconContainer>
              <SortingArrow
                onClick={() => sortProduct(key, 'asc')}
                direction="top"
                margin="0px 0 3px 0"
                color={
                  sortByHeader?.header === key &&
                    sortByHeader?.direction === 'asc'
                    ? Color.black
                    : Color.darkGray
                }
              />
              <SortingArrow
                onClick={() => sortProduct(key, 'desc')}
                direction="bottom"
                color={
                  sortByHeader?.header === key &&
                    sortByHeader?.direction === 'desc'
                    ? Color.black
                    : Color.darkGray
                }
              />
            </IconContainer>
          )}
          {label}
        </ThContents>
      </Th>
    );
  });

  const bodyRows = tableRows.map((row, i) => (
    <VariantTableRowV2
      key={i}
      columns={columns}
      errorInventoryTypes={row.errorInventoryTypes}
      errorTypes={row.errorTypes}
      stockFlag={false}
      focused={row.focused}
      focusedMode={false}
      onRowDelete={() => { }}
      onCellEdit={handleCellEdit}
      onBlur={onCellBlur}
      showDeleteButton={false}
      variant={row.variant}
      suggestionValuesList={suggestionValuesList}
      attributes={product?.attributes?.filter((x) => x.isSelectable) || []}
      classifications={
        product?.attributes?.filter((x) => !x.isSelectable) || []
      }
      clearSuggestions={clearSuggestions}
      originalId={product?.id}
      product={product!}
      disabled={isEditable ? false : true}
      publishedSKUList={[]}
      selectedWarehouses={selectedWarehouses}
      productStocks={productStocks}
      availableCountries={availableCountriesPurchase}
      isErrorColumn={isErrorColumn}
      selectedVariants={selectedVariants}
    />
  ));

  const parentColumns = (
    <Headers>
      <ThAction></ThAction>
      {parentHeaders.map((ph, index) =>
        ph.columns.length ? (
          <ThParent
            key={ph.key}
            colSpan={ph.colSpan}
            className={
              index === 0
                ? 'th-parent'
                : index === parentHeaders.length - 1
                  ? 'th-parent-right'
                  : ''
            }
          >
            <ThContentsParent className="heading">{ph.name}</ThContentsParent>
          </ThParent>
        ) : null
      )}
    </Headers>
  );

  const childColumns = (
    <Headers>
      <ThAction>{TEXT_LABELS.ERRORS}</ThAction>
      {columnHeaders}
    </Headers>
  );

  if (!product) return null;

  return (
    <CollapsibleTableContainer>
      <CollapsibleHeader onClick={() => setIsExpanded(!isExpanded)}>
        <CollapsibleTitle>
          <CollapsibleTitleText>
            {tableTitle || `Review & Select Variants to ${actionType?.toUpperCase()}`}
          </CollapsibleTitleText>
        </CollapsibleTitle>
        <CollapseButton
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <ExpandLess style={{ fontSize: '1.25rem' }} />
          ) : (
            <ExpandMore style={{ fontSize: '1.25rem' }} />
          )}
        </CollapseButton>
      </CollapsibleHeader>

      <CollapsibleContent isExpanded={isExpanded}>
        {allTableRows.length > 0 ? (
          <>
            <Container className="table-container-black">
              <Suspense
                fallback={
                  <LoadingIndicator margin="0 0 0 420px !important" size={35} />
                }
              >
                <BaseTable>
                  <Thead>
                    {parentColumns}
                    {childColumns}
                  </Thead>
                  <tbody>{bodyRows}</tbody>
                </BaseTable>
              </Suspense>
            </Container>
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={(size: number) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              totalPages={totalPages}
              selectorOptions={smartVariantsPaginationSelectOptions}
              baseRoute=""
              shouldRedirect={false}
              shouldHandleOnSizeChange={false}
            />
          </>
        ) : (
          <EmptyStateMessage>No variants to preview</EmptyStateMessage>
        )}
      </CollapsibleContent>
    </CollapsibleTableContainer>
  );
};

export default GeneratedVariantsTable;
